import {FC, useEffect, useState} from 'react';
import * as React from 'react';
import {NavLink} from 'react-router-dom';
import {Button} from '~/components/Button';
import {Trash2} from "lucide";
import {LucideIcon} from "~/components/LucideIcon";
import {tagListData, TagsList} from '~/components/Controls/Tags';
import {useConfirm} from '~/components/Dialogs';
import {onSubmitError} from '~/components/Form';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {LinksList} from '~/components/Controls/LinksList';
import style from '~/components/ResourcePage/index.module.scss';
import {TableRowActions, TableSort, TableSortDirection} from '~/components/Table';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {useDataSource} from '~/lib/dataSource';
import {Intent} from '~/lib/intent';
import {sortByNumber, sortByString} from '~/lib/sort';
import {clientsLinks} from '~/modules/Clients';
import {ClientsAbility} from '~/modules/Clients/ability';
import {sellerBlockingsAdapter} from '~/modules/Clients/adapters';
import {BlockingForm} from '~/modules/Clients/forms/BlockingForm';
import {defaultBlocking, getBlockingTypesLabel} from '~/modules/Clients/models/Blocking';
import { BlockingAgencyMode, IBlocking } from '~/modules/Clients/models/Blocking';

interface ISellerBlockingParams {
    sellerId: string;
}

export const SellerBlockings: FC<ISellerBlockingParams> = (props) => {

    const {state, fetchRecords, createRecord, updateRecord, deleteRecord} = useDataSource(sellerBlockingsAdapter);
    const ability = useAbility(ClientsAbility);
    const confirm = useConfirm();
    const modal = useModals();
    const [expand, setExpand] = useState(false);
    const maxItems = 3;

    const convertTextData = (record) => {
        record.blockingValues = tagListData(record.blockingValues);
        record.bundles = tagListData(record.bundles);
        return record;
    };

    const blockingCreate = modal.create(BlockingForm, {
        lockSeller: true,
        showAgencyField: ability.isAdmin(),
        onSubmit: (blocking, formikActions, close) => {
            createRecord(convertTextData(blocking))
                .then(close).catch(onSubmitError(formikActions));
        }
    });

    const blockingEdit = modal.create(BlockingForm, {
        lockSeller: true,
        showAgencyField: ability.isAdmin(),
        onSubmit: (blocking, formikActions, close) => {
            updateRecord(convertTextData(blocking))
                .then(close).catch(onSubmitError(formikActions));
        }
    });

    const resolveAgencyMode = (b: IBlocking): BlockingAgencyMode => {
    if (b.agencyMode) return b.agencyMode;
    if (!b.agencyIds || b.agencyIds.length === 0) return BlockingAgencyMode.ALL;
    return b.includesAgencies ? BlockingAgencyMode.INCLUDE : BlockingAgencyMode.EXCLUDE;
    };

    const fetchBlockings = () => fetchRecords({sellerId: props.sellerId});

    const recordsFilter = (record) => {
        if (typeof record.blockingValues === 'object') {
            record.blockingValues = record.blockingValues.join('\n');
        }
        if (typeof record.bundles === 'object') {
            record.bundles = record.bundles.join('\n');
        }
        return record;
    };

    useEffect(fetchBlockings, []);

    const expandButton = state.records.length > maxItems && <Button
        responsive
        className={style.buttonDashedLink}
        onClick={() => setExpand(!expand)}
    >{expand ? '- Hide' : '+ Expand'}</Button>;

    return <>
        <Title options={expandButton}
            toolbar={<>{ability.canCreateBlocking() && <Button intent={Intent.PRIMARY} responsive
                key='create'
                leftIcon={<i className='fa fa-edit'/>}
                disabled={state.isPending}
                onClick={() => blockingCreate.show({
                    blocking: {
                        ...defaultBlocking,
                        sellerId: Number(props.sellerId)
                    }
                })}>
                   Add
            </Button>}
            <Button intent={Intent.SUCCESS} responsive
                key='refresh'
                disabled={state.isPending}
                leftIcon={<i className='fa fa-refresh'/>}
                onClick={fetchBlockings}>
                       Refresh
            </Button>

            </>}>Blockings</Title>
        <TableSort defaultSort={{
            sort: sortByNumber,
            field: 'id',
            direction: TableSortDirection.DESC
        }}
        header
        columns={[{
            field: 'id',
            header: 'ID',
            sort: sortByNumber,
            small: true
        }, {
            field: 'blockingType',
            header: 'Type',
            sort: sortByString,
            cell: blocking => getBlockingTypesLabel(blocking.blockingType)
        }, {
            field: 'agencyIds',
            header: 'Agencies',
            responsive: true,
            hide: !ability.isAdmin(),
            cell: (b: IBlocking) => {
                const mode = resolveAgencyMode(b);
                if (mode === BlockingAgencyMode.ALL) {
                return <span className={style.muted}>All selected</span>;
                }
                return (
                <div className={style.links}>
                    <span className={style.mode}>{mode}</span>
                    <LinksList
                    values={(b.agencyIds ?? []).map(String)}
                    maxItems={3}
                    buildTo={(id) => clientsLinks.agency(id)}
                    />
                </div>
                );
            }
        },{
            field: 'reason',
            header: 'Reason',
            sort: sortByString,
            responsive: true,
            cellWrap: 'clamp-2',
            cell: (blocking: IBlocking) => blocking.reason?.trim()
                ? blocking.reason
                : <span className={style.muted}>—</span>
        }, {
            field: 'bundles',
            header: 'Apps',
            cell: blocking => <TagsList values={tagListData(blocking.bundles)} maxItems={3} size="small"/>,
            short: true
        }, {
            field: 'blockingValues',
            header: 'Values',
            cell: blocking => <TagsList values={tagListData(blocking.blockingValues)} maxItems={3} size="small"/>,
            short: true
        }, {
            small: true,
            cell: (blocking) => <TableRowActions>
                {ability.canEditBlocking(blocking) && (
                    <Button
                        small
                        variant="menu"
                        onClick={() => blockingEdit.show({blocking})}
                        leftIcon={<i className='fa fa-edit'/>}
                    >
                        Edit
                    </Button>
                )}
                {ability.canDeleteBlocking(blocking) && (
                    <Button
                        small
                        variant="menu"
                        intent={Intent.DANGER}
                        onClick={() => confirm('Delete blocking?', {
                            onOk: () => deleteRecord(blocking),
                            icon: <LucideIcon icon={Trash2} size={20}/>,
                            intent: Intent.DANGER
                        })}
                        leftIcon={<i className='fa fa-trash'/>}
                    >
                        Delete
                    </Button>
                )}
            </TableRowActions>
        }]} records={state.records.filter(recordsFilter)}
        maxItems={expand ? state.records.length : maxItems} placeholder={state.isPending && <Progress/>}/>
    </>;
};
